import { useMutation } from "@tanstack/react-query";
import { ArrowUpRight, LoaderCircle, MonitorPlay, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { countdown } from "../../lib/format";
import { useIsManager } from "../../lib/permissions";
import { PREVIEW_EXTEND_LABEL, PREVIEW_EXTEND_MINUTES } from "../../lib/preview";
import type { IssueDetail } from "../../types";
import { TimeAgo } from "../time-ago";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

/** Live remaining time for an active preview. Ticks each second. */
function PreviewExpiry({ expiresAt }: { expiresAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const remaining = countdown(expiresAt, now);
  return (
    <span className="text-muted-foreground tabular-nums">
      {remaining === "now" ? "expiring" : `expires in ${remaining}`}
    </span>
  );
}

/**
 * Live state of the newest preview environment, full width above the Factory
 * card: Create, phase spinner + Stop, Open links + Extend + Stop, or Retry.
 */
export function PreviewEnvironment({ issue, onChanged }: { issue: IssueDetail; onChanged: () => void }) {
  const manager = useIsManager();
  const done = issue.statusKey === "done";
  const preview = issue.preview ?? null;
  const create = useMutation({
    mutationFn: () => api.createIssuePreview(issue.identifier),
    onSuccess: () => {
      toast.success("Preview requested — it takes a few minutes to build");
      onChanged();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Preview request failed"),
  });
  const stop = useMutation({
    mutationFn: (id: string) => api.stopPreview(id),
    onSuccess: () => {
      toast.success("Preview stopped");
      onChanged();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Stop failed"),
  });
  const extend = useMutation({
    mutationFn: (id: string) => api.extendPreview(id, PREVIEW_EXTEND_MINUTES),
    onSuccess: () => {
      toast.success("Preview extended by 3 days");
      onChanged();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Extend failed"),
  });

  const active = preview && ["requested", "provisioning", "ready", "updating"].includes(preview.status);
  const createLabel = preview?.canRetry ? "Retry preview" : "Create preview";

  return (
    <div className="w-full">
      {!preview && (
        <Card className="gap-0 overflow-hidden py-0">
          <CardHeader className="gap-1 px-4 py-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <MonitorPlay className="size-4 text-muted-foreground" />
              Preview environment
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {done ? (
                "This issue is done, so a preview cannot be created."
              ) : (
                <>
                  Launch a disposable environment for this pull request. It expires in 3 days, or in one week
                  when the issue is 55 story points or more.
                </>
              )}
            </p>
          </CardHeader>
          {!done && (
            <div className="border-t bg-muted/20 px-3 py-2.5">
              <Button
                variant="outline"
                size="sm"
                className="w-full bg-background"
                disabled={!issue.prUrl || create.isPending}
                title={issue.prUrl ? undefined : "The issue has no pull request yet"}
                onClick={() => create.mutate()}
              >
                <MonitorPlay className="size-4" /> Create preview
              </Button>
            </div>
          )}
        </Card>
      )}

      {preview &&
        (preview.status === "requested" ||
          preview.status === "provisioning" ||
          preview.status === "updating") && (
          <Card className="gap-0 overflow-hidden py-0">
            <CardHeader className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <MonitorPlay className="size-4 text-muted-foreground" />
                    Preview environment
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {preview.phase ?? "Preparing the environment"}
                  </p>
                </div>
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-sky-500/10 px-2 py-1 text-[11px] font-medium text-sky-700 dark:text-sky-300">
                  <LoaderCircle className="size-3 animate-spin" />
                  {preview.status === "updating" ? "Updating" : "Building"}
                </span>
              </div>
            </CardHeader>
            {preview.statusMessage && (
              <CardContent className="border-t px-4 py-3">
                <p className="text-xs text-muted-foreground">{preview.statusMessage}</p>
              </CardContent>
            )}
            <div className="flex items-center gap-2 border-t bg-muted/20 px-3 py-2">
              <Button variant="outline" size="sm" className="h-7 bg-background text-xs" asChild>
                <Link to={`/previews/${preview.id}`}>View progress</Link>
              </Button>
              <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
                Updated <TimeAgo at={preview.updatedAt} />
              </span>
              {manager && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  disabled={stop.isPending}
                  onClick={() => stop.mutate(preview.id)}
                >
                  Stop
                </Button>
              )}
            </div>
          </Card>
        )}

      {preview?.status === "ready" && (
        <Card className="gap-0 overflow-hidden py-0">
          <CardHeader className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <MonitorPlay className="size-4 text-muted-foreground" />
                  Preview environment
                </CardTitle>
                <div className="flex items-center gap-2 text-xs">
                  {preview.expiresAt && <PreviewExpiry expiresAt={preview.expiresAt} />}
                  {preview.headSha && (
                    <>
                      <span className="text-border">•</span>
                      <span className="font-mono text-muted-foreground">{preview.headSha.slice(0, 7)}</span>
                    </>
                  )}
                </div>
              </div>
              <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                <span className="size-1.5 rounded-full bg-emerald-500" />
                Ready
              </span>
            </div>
          </CardHeader>
          <CardContent className="border-t px-4 py-3">
            <p className="mb-2 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
              Open application
            </p>
            <div className="grid grid-cols-3 gap-2">
              {(["admin", "client", "employer"] as const).map((app) =>
                preview.apps?.[app] ? (
                  <Button
                    key={app}
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1 bg-background px-2 text-xs"
                    asChild
                  >
                    <a href={preview.apps[app]} target="_blank" rel="noreferrer">
                      {app.charAt(0).toUpperCase() + app.slice(1)}
                      <ArrowUpRight className="size-3" />
                    </a>
                  </Button>
                ) : null,
              )}
            </div>
          </CardContent>
          <div className="flex gap-2 border-t bg-muted/20 px-3 py-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 bg-background px-2 text-xs"
              disabled={extend.isPending}
              onClick={() => extend.mutate(preview.id)}
            >
              {PREVIEW_EXTEND_LABEL}
            </Button>
            {manager && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 px-2 text-xs"
                disabled={stop.isPending}
                onClick={() => stop.mutate(preview.id)}
              >
                Stop
              </Button>
            )}
          </div>
        </Card>
      )}

      {preview && !active && (
        <Card className="gap-0 overflow-hidden py-0">
          <CardHeader className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <MonitorPlay className="size-4 text-muted-foreground" />
                  Preview environment
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Updated <TimeAgo at={preview.updatedAt} />
                </p>
              </div>
              <span
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-medium ${
                  preview.status === "failed"
                    ? "bg-red-500/10 text-red-700 dark:text-red-300"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <span
                  className={`size-1.5 rounded-full ${
                    preview.status === "failed" ? "bg-red-500" : "bg-muted-foreground/50"
                  }`}
                />
                {preview.status === "failed"
                  ? "Failed"
                  : preview.status === "expired"
                    ? "Expired"
                    : "Stopped"}
              </span>
            </div>
          </CardHeader>
          {(preview.lastError || preview.stopReason) && (
            <CardContent className="border-t px-4 py-3">
              <p
                className={`text-xs ${preview.lastError ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}
              >
                {preview.lastError ?? preview.stopReason}
              </p>
            </CardContent>
          )}
          <div className="border-t bg-muted/20 px-3 py-2.5">
            {done ? (
              <p className="text-xs text-muted-foreground">
                This issue is done, so a preview cannot be created.
              </p>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-full bg-background text-xs"
                disabled={!issue.prUrl || create.isPending}
                onClick={() => create.mutate()}
              >
                <RotateCcw className="size-3.5" /> {createLabel}
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
